/**
 * Implied Facts Review Modal
 *
 * Pops AFTER the Edit Resume lightbox closes, once the async extraction has
 * landed new proposals. Users Save many times but Close once, so this is the
 * one moment where interrupting them for a quick confirm is worth it.
 *
 * Confirmed facts become GROUND TRUTH: the summary, highlights and cover-letter
 * prompts receive them so the year math is never re-derived by a small model.
 *
 * The trash button is a "not right" — it DISMISSES the proposal (a hidden
 * tombstone) rather than deleting it, so the next extraction run suppresses the
 * same claim instead of re-asking. See `dismissFact` in utils/impliedFacts.
 *
 * PER-ITEM ACTIONS COMMIT IMMEDIATELY; CLOSING COMMITS NOTHING
 * -----------------------------------------------------------
 * (owner contract, 2026-08-08). Confirm, trash and an edit commit are ANSWERS —
 * asked and answered — so each one is applied to the working list AND saved to
 * the server right then. Editing a fact commits the correction as a CONFIRMED,
 * user-owned fact (`updateFactText` does both), because rewriting a claim is a
 * stronger answer than agreeing with it.
 *
 * Closing — the X button, Escape, and "Review later" — saves NOTHING. An
 * accidental keystroke must never mutate stored data. Proposals nobody acted on
 * stay `proposed` and may be re-asked after a later run; everything the user did
 * act on has already landed. "Done" still saves the working list, which by then
 * is a no-op re-write of what the per-item saves already stored.
 *
 * This replaced "only the Done button saves": trashing a fact and then closing
 * with X/Escape used to discard the decision silently, so no tombstone was ever
 * written and the very next extraction re-proposed the same claim — the owner
 * was asked about "19 years of experience in machine learning" forever (round 2
 * of the LEARNINGS entry "An LLM That Can't See Its Prior Output Will Re-Derive
 * It, Drifted").
 *
 * SAVE SERIALIZATION: at most ONE save is in flight; a decision taken while one
 * is running is queued as the newest-wins list and written by the same drain, so
 * a rapid trash-trash-confirm can't race itself into a stale write. A failed
 * save rolls the list back to what the SERVER last accepted and shows the error,
 * so the item reappears and is actionable — a decision is never dropped quietly.
 *
 * PORTALED to document.body on purpose (LEARNINGS: a `fixed inset-0` overlay
 * rendered inside a transformed/animated ancestor positions against that
 * ancestor, not the viewport — z-index cannot rescue it).
 */
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, CheckCheck, Pencil, Sparkles, Trash2, X } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import {
  confirmAllFacts, confirmFact, createFactSaveQueue, dismissFact, proposedFacts,
  updateFactText,
} from '../utils/impliedFacts';
import type { FactSaveQueue } from '../utils/impliedFacts';
import type { ImpliedFact } from '../types';

interface Props {
  isOpen: boolean;
  resumename: string;
  /** The full stored list — proposals are shown, confirmed ones ride along. */
  facts: ImpliedFact[];
  /**
   * Persist the list (parent PUTs the resume with only facts changed). Called
   * on EVERY per-item action, so it must NOT close the modal — closing is
   * `onDismiss`, and the modal decides when that happens.
   */
  onSave: (facts: ImpliedFact[]) => Promise<void>;
  /** Close. Never saves — see the contract above. */
  onDismiss: () => void;
}

export function ImpliedFactsReviewModal({ isOpen, resumename, facts, onSave, onDismiss }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [working, setWorking] = useState<ImpliedFact[]>(facts);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mutations read/write through a ref as well as state: two clicks landing in
  // one tick must both build on each other, not on the last rendered list.
  const workingRef = useRef<ImpliedFact[]>(facts);
  // `onSave` is a fresh closure every parent render; the queue outlives them.
  const saveRef = useRef(onSave);
  saveRef.current = onSave;

  // One in-flight save, newest-wins queue (see SAVE SERIALIZATION).
  const queueRef = useRef<FactSaveQueue | null>(null);
  if (!queueRef.current) {
    queueRef.current = createFactSaveQueue(
      (next) => saveRef.current(next),
      {
        onSavingChange: setSaving,
        onFailure: (lastSaved) => {
          // Roll back to the server's truth so the un-saved decision is visible
          // and actionable again rather than silently "done" in the UI only.
          setError('Could not save those facts. They are still here — try again.');
          workingRef.current = lastSaved;
          setWorking(lastSaved);
        },
      },
      facts,
    );
  }

  useEscapeClose(onDismiss, isOpen);
  useFocusTrap(modalRef, isOpen);

  // Re-seed when a new run hands us a different list (the modal is keyed by
  // resume in the parent, so this only fires for a genuinely new proposal set).
  const seedRef = useRef(facts);
  if (seedRef.current !== facts) {
    seedRef.current = facts;
    workingRef.current = facts;
    queueRef.current.reseed(facts);
    setWorking(facts);
    setEditingId(null);
  }

  if (!isOpen) return null;

  const pending = proposedFacts(working);

  /** Queue `next` and return the promise of the drain that will write it. */
  function persist(next: ImpliedFact[]): Promise<boolean> {
    setError(null);
    return queueRef.current!.push(next);
  }

  /**
   * A per-item ANSWER: apply it locally and commit it immediately. Every
   * confirm / trash / edit-commit goes through here, so no decision can depend
   * on the user finding the Done button afterwards.
   */
  function act(fn: (prev: ImpliedFact[]) => ImpliedFact[]) {
    const next = fn(workingRef.current);
    workingRef.current = next;
    setWorking(next);
    void persist(next);
  }

  function commitEdit(id: string) {
    // Opening the pencil and clicking away is NOT an answer: an unchanged (or
    // emptied) draft cancels the edit without confirming or saving anything —
    // same accidental-keystroke rule as closing the modal.
    const current = workingRef.current.find(f => f.id === id);
    const next = draft.trim();
    if (current && next && next !== current.fact) {
      // `updateFactText` marks the fact confirmed + user-owned: a correction
      // is the strongest form of "asked and answered".
      act(prev => updateFactText(prev, id, draft));
    }
    setEditingId(null);
    setDraft('');
  }

  /** Done — save the whole list (a no-op re-write in practice), then close. */
  async function finish() {
    if (await persist(workingRef.current)) onDismiss();
  }

  const body = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Review implied facts"
      data-testid="implied-facts-review"
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between p-4 border-b">
          <div className="flex items-start gap-2">
            <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">A few facts worth confirming</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Snappy worked these out from &ldquo;{resumename}&rdquo; &mdash; they are implied by your
                resume, not written on it. Confirm the true ones and we&rsquo;ll keep every tailored
                resume consistent with them.
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            aria-label="Close"
            data-help-target="mainapp.impliedfactsreview.close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-2">
          {pending.length === 0 && (
            <p className="text-sm text-gray-500 italic">
              Nothing left to review. You can add or edit facts any time in Edit Resume.
            </p>
          )}
          {pending.map(f => (
            <div key={f.id} className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200">
              {editingId === f.id ? (
                <input
                  type="text"
                  value={draft}
                  autoFocus
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commitEdit(f.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(f.id); }}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  autoComplete="off"
                  aria-label="Edit fact"
                />
              ) : (
                <span className="flex-1 text-sm text-gray-800">{f.fact}</span>
              )}
              <button
                onClick={() => act(prev => confirmFact(prev, f.id))}
                className="text-green-600 hover:text-green-700 flex-shrink-0"
                title="Confirm this fact"
                aria-label={`Confirm: ${f.fact}`}
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setEditingId(f.id); setDraft(f.fact); }}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                title="Edit this fact"
                aria-label={`Edit: ${f.fact}`}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => act(prev => dismissFact(prev, f.id))}
                className="text-gray-500 hover:text-red-500 flex-shrink-0"
                title="Not right — don't ask about this again"
                aria-label={`Dismiss: ${f.fact}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t">
          <button
            onClick={onDismiss}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Review later
          </button>
          <div className="flex gap-2">
            {pending.length > 0 && (
              <button
                onClick={() => act(prev => confirmAllFacts(prev))}
                className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1"
              >
                <CheckCheck className="w-4 h-4" />
                Confirm all
              </button>
            )}
            <button
              onClick={() => { void finish(); }}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

export default ImpliedFactsReviewModal;
